import { useState, useCallback, useEffect } from 'react';
import { apiCall } from '../../services/apiUtil';

export const useSquads = (shouldLoad = true) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchGroups = useCallback(async (filters = {}, append = false) => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.sportTypeId) queryParams.append('sportTypeId', filters.sportTypeId);
      if (filters.page) queryParams.append('page', filters.page);
      queryParams.append('limit', filters.limit || 10);

      const query = queryParams.toString();
      const result = await apiCall(`/groups/all${query ? `?${query}` : ''}`);

      if (result.success && result.data) {
        if (append) {
          setGroups(prev => [...prev, ...result.data]);
        } else {
          setGroups(result.data);
        }
      } else {
        if (!append) {
          setGroups([]);
        }
      }
      return result;
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError(err);
      if (!append) {
        setGroups([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserGroups = useCallback(async (filters = {}, append = false) => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.sportTypeId) queryParams.append('sportTypeId', filters.sportTypeId);
      if (filters.page) queryParams.append('page', filters.page);
      queryParams.append('limit', filters.limit || 10);

      const query = queryParams.toString();
      const result = await apiCall(`/groups/my${query ? `?${query}` : ''}`);

      if (result.success && result.data) {
        if (append) {
          setGroups(prev => [...prev, ...result.data]);
        } else {
          setGroups(result.data);
        }
      } else {
        if (!append) {
          setGroups([]);
        }
      }
      return result;
    } catch (err) {
      console.error('Error fetching user groups:', err);
      setError(err);
      if (!append) {
        setGroups([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const createGroup = useCallback(async (groupData) => {
    try {
      const result = await apiCall('/groups', {
        method: 'POST',
        body: JSON.stringify(groupData),
      });

      if (result.success) {
        setGroups(prev => [result.data, ...prev]);
        return { success: true, data: result.data };
      }
      throw new Error(result.message || 'Failed to create group');
    } catch (err) {
      console.error('Error creating group:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const joinGroup = useCallback(async (groupId) => {
    try {
      const result = await apiCall(`/groups/${groupId}/join`, {
        method: 'POST',
        body: JSON.stringify({}),
      });


      if (result.success) {
        const isPending = result.data?.status === 'pending';

        setGroups(prev => {
          return prev.map(group =>
            group.id === groupId
              ? {
                  ...group,
                  isMember: !isPending,
                  memberStatus: isPending ? 'pending' : 'active',
                  memberCount: isPending ? group.memberCount : group.memberCount + 1,
                  userRole: isPending ? null : 'member',
                }
              : group
          );
        });
        return { success: true, pending: isPending };
      }
      throw new Error(result.message || 'Failed to join group');
    } catch (err) {
      console.error('Error joining group:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const leaveGroup = useCallback(async (groupId) => {
    try {
      const result = await apiCall(`/groups/${groupId}/leave`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (result.success) {
        setGroups(prev => prev.map(group =>
          group.id === groupId
            ? { ...group, isMember: false, memberCount: Math.max(0, group.memberCount - 1), userRole: null }
            : group
        ));
        return { success: true };
      }
      throw new Error(result.message || 'Failed to leave group');
    } catch (err) {
      console.error('Error leaving group:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const getGroup = useCallback(async (groupId) => {
    try {
      const result = await apiCall(`/groups/${groupId}`);
      return result.success
        ? { success: true, data: result.data }
        : { success: false, error: result.message };
    } catch (err) {
      console.error('Error fetching group:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const updateGroup = useCallback(async (groupId, groupData) => {
    try {
      const result = await apiCall(`/groups/${groupId}`, {
        method: 'PUT',
        body: JSON.stringify(groupData),
      });
      return result.success
        ? { success: true, data: result.data }
        : { success: false, error: result.message };
    } catch (err) {
      console.error('Error updating group:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (shouldLoad) {
      fetchGroups();
    }
  }, [shouldLoad, fetchGroups]);

  return {
    groups,
    loading,
    error,
    fetchGroups,
    fetchUserGroups,
    createGroup,
    updateGroup,
    joinGroup,
    leaveGroup,
    getGroup,
    refreshGroups: fetchGroups,

    searchUsers: useCallback(async (query, page = 1, limit = 10) => {
      try {
        const params = new URLSearchParams({
          search: query,
          page: page.toString(),
          limit: limit.toString(),
        });
        const result = await apiCall(`/user?${params}`);
        return { success: true, data: result.data };
      } catch (err) {
        console.error('Error searching users:', err);
        return { success: false, error: err.message };
      }
    }, []),

    getGroupMembers: useCallback(async (groupId, page = 1, limit = 20) => {
      try {
        const result = await apiCall(`/groups/${groupId}/members?page=${page}&limit=${limit}`);
        return result.success
          ? { success: true, data: result.data }
          : { success: false, error: result.message };
      } catch (err) {
        console.error('Error fetching group members:', err);
        return { success: false, error: err.message };
      }
    }, []),

    addMember: useCallback(async (groupId, userId) => {
      try {
        const result = await apiCall(`/groups/${groupId}/members`, {
          method: 'POST',
          body: JSON.stringify({ targetUserId: userId }),
        });
        return result.success
          ? { success: true, data: result.data }
          : { success: false, error: result.message };
      } catch (err) {
        console.error('Error adding member:', err);
        return { success: false, error: err.message };
      }
    }, []),

    updateMemberRole: useCallback(async (groupId, userId, role) => {
      try {
        const result = await apiCall(`/groups/${groupId}/members/role`, {
          method: 'PUT',
          body: JSON.stringify({ targetUserId: userId, role }),
        });
        return result.success
          ? { success: true, data: result.data }
          : { success: false, error: result.message };
      } catch (err) {
        console.error('Error updating member role:', err);
        return { success: false, error: err.message };
      }
    }, []),

    removeMember: useCallback(async (groupId, userId) => {
      try {
        const result = await apiCall(`/groups/${groupId}/members/${userId}`, {
          method: 'DELETE',
        });
        return result.success
          ? { success: true, data: result.data }
          : { success: false, error: result.message };
      } catch (err) {
        console.error('Error removing member:', err);
        return { success: false, error: err.message };
      }
    }, []),

    getPendingJoinRequests: useCallback(async (groupId) => {
      try {
        const result = await apiCall(`/groups/${groupId}/join-requests`);
        return result.success
          ? { success: true, data: result.data }
          : { success: false, error: result.message };
      } catch (err) {
        console.error('Error fetching pending join requests:', err);
        return { success: false, error: err.message };
      }
    }, []),

    acceptJoinRequest: useCallback(async (groupId, userId) => {
      try {
        const result = await apiCall(`/groups/${groupId}/join-requests/${userId}/accept`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        return result.success
          ? { success: true, data: result.data }
          : { success: false, error: result.message };
      } catch (err) {
        console.error('Error accepting join request:', err);
        return { success: false, error: err.message };
      }
    }, []),

    rejectJoinRequest: useCallback(async (groupId, userId) => {
      try {
        const result = await apiCall(`/groups/${groupId}/join-requests/${userId}`, {
          method: 'DELETE',
        });
        return result.success
          ? { success: true, data: result.data }
          : { success: false, error: result.message };
      } catch (err) {
        console.error('Error rejecting join request:', err);
        return { success: false, error: err.message };
      }
    }, []),

    deleteGroup: useCallback(async (groupId) => {
      try {
        const result = await apiCall(`/groups/${groupId}`, { method: 'DELETE' });
        return result.success
          ? { success: true }
          : { success: false, error: result.message };
      } catch (err) {
        console.error('Error deleting squad:', err);
        return { success: false, error: err.message };
      }
    }, []),
  };
};
